import { useCallback, useMemo } from 'react';
import { getSubscribedPlanUseCase } from '../use-cases/get-subscribed-plan.use-case';
import { subscribedPlanAtom } from '../states/subscribed-plan.atom';
import useQuery from '../../../../shared/hooks/useQuery';
import { PlanAddon, SubscribedPlan } from '../typings';

export function useSubscribedPlan(autoFetch = true) {
  const {
    requestData: { data: subscribedPlan, loading, error },
    setData,
    fetchData,
  } = useQuery<SubscribedPlan>({
    queryFunction: getSubscribedPlanUseCase,
    requestAtom: subscribedPlanAtom,
    autoFetch,
  });

  const addons = subscribedPlan?.addons ?? [];
  const trialAddons = subscribedPlan?.trialAddons ?? [];

  const shouldMakePayment = useCallback(() => {
    return subscribedPlan?.canMakeNextPayment;
  }, [subscribedPlan]);

  const askToContactSupport = useMemo(() => {
    return subscribedPlan?.canMakeNextPayment ? subscribedPlan.function?.type : undefined;
  }, [subscribedPlan]);

  const verifySubscribedPlan = useCallback(async () => {
    if (typeof subscribedPlan?.canMakeNextPayment === 'boolean') {
      return subscribedPlan.canMakeNextPayment;
    }

    const response = await fetchData();
    const newSubscribedPlanData = response?.data;
    if (typeof newSubscribedPlanData?.canMakeNextPayment === 'boolean') {
      return newSubscribedPlanData.canMakeNextPayment;
    }

    return false;
  }, [subscribedPlan, fetchData]);

  /** Has active (non-expired) addon in addons list */
  const isAddonPresent = useCallback(
    (addonId?: string) => {
      if (!addonId) return false;
      return addons.some((addon) => addon.addonId === addonId && new Date(addon.endDate) > new Date());
    },
    [addons]
  );

  /** Get trial addon entry if present (any expiry) */
  const getTrialAddon = useCallback(
    (addonId: string): PlanAddon | undefined => trialAddons.find((a) => a.addonId === addonId),
    [trialAddons]
  );

  /** Has any trial addon entry (used or active) */
  const hasTrialAddon = useCallback((addonId: string) => Boolean(getTrialAddon(addonId)), [getTrialAddon]);

  /** Trial is currently active (trial addon exists and endDate > now) */
  const isTrialActive = useCallback(
    (addonId: string) => {
      const trial = getTrialAddon(addonId);
      return trial ? new Date(trial.endDate) > new Date() : false;
    },
    [getTrialAddon]
  );

  /** Can show "Start free trial" CTA: no active addon and never had a trial (not in trialAddons) */
  const canShowFreeTrial = useCallback(
    (addonId: string) => !isAddonPresent(addonId) && !hasTrialAddon(addonId),
    [isAddonPresent, hasTrialAddon]
  );

  /** Trial ended and not renewed: had trial, trial expired, no active addon (for "free trial ended" modal later) */
  const hasTrialEnded = useCallback(
    (addonId: string) => hasTrialAddon(addonId) && !isTrialActive(addonId) && !isAddonPresent(addonId),
    [hasTrialAddon, isTrialActive, isAddonPresent]
  );

  const isMediaHidden = Boolean(
    loading ||
      (subscribedPlan &&
        subscribedPlan?.piiSettings?.mediaPolicy === 'REJECT' &&
        (subscribedPlan?.piiSettings?.maskApiConfig?.media?.url ?? '').length === 0)
  );

  const isMediaHiddenForBroadcast = Boolean(
    loading || (subscribedPlan && subscribedPlan?.piiSettings?.mediaPolicy === 'REJECT')
  );

  const isPIITextHidden =
    loading || (subscribedPlan && ['REJECT', 'MASK'].includes(subscribedPlan?.piiSettings?.textPolicy || ''));

  return {
    subscribedPlan,
    verifySubscribedPlan,
    loading,
    askToContactSupport,
    error,
    shouldMakePayment,
    isAddonPresent,
    addons,
    trialAddons,
    getTrialAddon,
    hasTrialAddon,
    isTrialActive,
    canShowFreeTrial,
    hasTrialEnded,
    setSubscribedPlan: setData,
    isMediaHidden,
    isMediaHiddenForBroadcast,
    isPIITextHidden,
  };
}
